import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { z } from "zod"
import { cn } from "@/lib/utils"
import { Check, ChevronsUpDown } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { AppDispatch } from "@/redux"
import { useDispatch } from "react-redux"
import { getFormProps } from "@/helpers/formHelpers"
import { FilterProps } from "@/types/workflow"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { ComparisonOperators, FilterCode } from "@/constants/filters/filters"
import { useEffect, useState } from "react"
import { SelectInputField } from "@/components/custom/InputField/SelectInputField"
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { SelectOption } from "@/types/inputField"
import { AppField, FieldsResponse } from "@/types/WorkflowCreate/fields"

const formSchema = z.object({
  comparisonField: z.string().min(1, {
    message: 'Please select any field',
  }),
  operator: z.string().min(1, {
    message: 'Please select any comparison operator'
  }),
  value: z.string().min(1, {
    message: 'Please type any value',
  })
})

export const FieldComparison: React.FC<FilterProps> = ({
  code,
  stepId,
  order,
  selectedValue,
  selectOptions,
  name,
  description,
  availableFields
}: FilterProps) => {
  const dispatch: AppDispatch = useDispatch()
  const [open, setOpen] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      comparisonField: '',
      operator: '',
      value: '',
    },
  })
  const field = form.watch('comparisonField')
  const operator = form.watch('operator')
  const value = form.watch('value')
  const formProps = getFormProps(form, formSchema)

  useEffect(() => {
    if (selectedValue) {
      const [field, operator, value] = selectedValue.split(',')
      form.setValue('comparisonField', field)
      form.setValue('operator', operator)
      form.setValue('value', value)
    }
  }, [selectedValue, form])

  const extractFieldCodes = (
    fields: AppField[],
    codes: Set<string> = new Set()
  ): Set<string> => {
    fields.forEach((field) => {
      codes.add(field.code);
      if (field.fields && field.fields.length > 0) {
        extractFieldCodes(field.fields, codes);
      }
    });
    return codes;
  };

  const filterFields = (
    fields: SelectOption[],
    obj?: FieldsResponse
  ): SelectOption[] => {
    if (!obj) return fields;

    const allowedCodes = new Set<string>();
    extractFieldCodes(obj.requestFields, allowedCodes);
    extractFieldCodes(obj.responseFields, allowedCodes);

    return fields.filter(f => allowedCodes.has(f.id));
  };


  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          className="w-auto font-semibold"
          variant={ButtonVariant.DEFAULT}
        >
          Update
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="mb-6">
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col items-start">
                <h4 className="text-primary font-semibold text-xl leading-5 mb-2">
                  Filter Setup: {name}
                </h4>
                <p className="text-secondary text-sm mb-2 text-left font-normal">{description}</p>
              </div>
              <div className="flex items-center">
                <DialogClose asChild>
                  <Button variant={ButtonVariant.OUTLINE} className="mx-4">Cancel</Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button
                    type="submit"
                    disabled={operator?.length === 0 && value.length === 0}
                    onClick={() => {
                      dispatch(workflowActions.setStepFilter({
                        stepId,
                        order,
                        filter: code,
                        filterValue: `${field},${operator},${value}`,
                      }))
                      dispatch(workflowActions.setLastUpdatedFilter(code as FilterCode))
                    }}
                  >
                    Update Filter
                  </Button>
                </DialogClose>
              </div>
            </div>
          </DialogTitle>
          <div className="">
            <div className="my-4" />
            <Form {...form}>
              <form className="space-y-6 flex flex-col items-start">
                <div className="flex flex-row items-center">
                  <FormField
                    control={form.control}
                    name="comparisonField"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="flex justify-start font-semibold text-primary" >
                          Select Field
                        </FormLabel>
                        <Popover open={open} onOpenChange={setOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                role="combobox"
                                className={cn(
                                  INPUT_FIELD_CLASSNAME,
                                  "w-[260px] justify-between bg-foreground text-primary",
                                  !field.value && "text-muted"
                                )}
                              >
                                <span className="truncate">
                                  {field.value
                                    ? filterFields(selectOptions, availableFields).find(
                                      (option) => option.id === field.value
                                    )?.value
                                    : "Select Option"}
                                </span>
                                <ChevronsUpDown className="opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0">
                            <Command>
                              <CommandInput
                                placeholder="Search option..."
                                className="h-9"
                              />
                              <CommandList onWheel={e => e.stopPropagation()}>
                                <CommandEmpty>No options found.</CommandEmpty>
                                {filterFields(selectOptions, availableFields).map((option) => (
                                  <CommandItem
                                    value={option.value}
                                    key={option.id}
                                    onSelect={() => {
                                      form.setValue("comparisonField", option.id)
                                      setOpen(false)
                                    }}
                                  >
                                    {option.value}
                                    <Check
                                      className={cn(
                                        "ml-auto",
                                        option.value === field.value
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="mr-4" />
                  <SelectInputField
                    name="operator"
                    label="Comparison"
                    error={!!form.formState.errors.operator}
                    selectOptions={ComparisonOperators}
                    additionalClassName="w-[210px]"
                    isDisabled={false}
                    {...formProps}
                  />
                  <div className="mr-4" />
                  <TextInputField
                    name="value"
                    label="Value"
                    isDisabled={false}
                    additionalClassName={'w-[210px]'}
                    {...formProps}
                  />
                </div>
              </form>
            </Form>
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}
